import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ShoppingBag, MessageCircle, Truck, ShieldCheck, RotateCcw, Minus, Plus, Check } from "lucide-react";
import { getProduct, getRelated, getReviews, postReview } from "../lib/api";
import { formatFCFA, waLink } from "../lib/constants";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import GoldDivider from "../components/GoldDivider";
import Reveal from "../components/Reveal";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [tab, setTab] = useState("desc");
  const [form, setForm] = useState({ author: "", rating: 5, comment: "" });

  useEffect(() => {
    setQty(1);
    setActiveImg(0);
    getProduct(slug).then(setProduct).catch(() => navigate("/boutique"));
    getRelated(slug).then(setRelated);
    getReviews(slug).then(setReviews);
  }, [slug, navigate]);

  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-32 text-center text-neutral-400">Chargement…</div>;
  }

  const discount = product.old_price
    ? Math.round(((product.old_price - product.price) / product.old_price) * 100)
    : 0;

  const waMessage = `Bonjour MaxiChoix ! Je souhaite commander :\n\n*${product.name}*\nQuantité : ${qty}\nPrix : ${formatFCFA(product.price)}\n\nMerci de me confirmer la disponibilité.`;

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || !form.comment.trim()) return;
    const rev = await postReview({ product_slug: slug, ...form });
    setReviews([rev, ...reviews]);
    setForm({ author: "", rating: 5, comment: "" });
    toast.success("Merci pour votre avis !");
  };

  return (
    <div data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8 text-sm text-neutral-500">
        <Link to="/" className="hover:text-[#B8860B]">Accueil</Link> /{" "}
        <Link to={`/boutique?category=${product.category}`} className="hover:text-[#B8860B]">{product.category_name}</Link> /{" "}
        <span className="text-[#0A0A0A]">{product.name}</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <motion.div
            key={activeImg}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            className="bg-[#FBF9F4] rounded-lg overflow-hidden aspect-square mb-4"
          >
            <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </motion.div>
          {product.images.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded overflow-hidden border-2 transition ${activeImg === i ? "border-[#B8860B]" : "border-transparent"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8860B] mb-3">{product.category_name}</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#0A0A0A] leading-tight mb-4">{product.name}</h1>

          <div className="flex items-center gap-2 mb-6">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className={i < Math.round(product.rating) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-neutral-300"} />
              ))}
            </div>
            <span className="text-sm text-neutral-500">{product.rating} • {product.reviews_count} avis</span>
          </div>

          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-display text-4xl font-semibold text-[#0A0A0A]">{formatFCFA(product.price)}</span>
            {product.old_price && (
              <>
                <span className="text-lg text-neutral-400 line-through">{formatFCFA(product.old_price)}</span>
                <span className="bg-[#B8860B] text-white text-xs font-semibold px-2 py-1">-{discount}%</span>
              </>
            )}
          </div>

          <p className="text-neutral-600 leading-relaxed mb-6">{product.short_description}</p>

          <ul className="space-y-2 mb-8">
            {product.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-neutral-700">
                <Check size={16} className="text-[#B8860B]" /> {f}
              </li>
            ))}
          </ul>

          {/* Quantity + actions */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center border border-neutral-200 rounded-sm">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3 text-neutral-600 hover:text-[#B8860B]" data-testid="qty-minus">
                <Minus size={16} />
              </button>
              <span className="w-10 text-center font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-3 text-neutral-600 hover:text-[#B8860B]" data-testid="qty-plus">
                <Plus size={16} />
              </button>
            </div>
            <button
              onClick={() => addItem(product, qty)}
              className="btn-gold flex-1 py-4 rounded-sm text-base"
              data-testid="detail-add-to-cart"
            >
              <ShoppingBag size={18} /> Ajouter au panier
            </button>
          </div>

          <a
            href={waLink(waMessage)}
            target="_blank" rel="noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-4 rounded-sm font-medium hover:bg-[#1fbb59] transition mb-8"
            data-testid="detail-whatsapp-order"
          >
            <MessageCircle size={18} /> Commander via WhatsApp
          </a>

          <div className="grid grid-cols-3 gap-4 border-t border-neutral-100 pt-6">
            {[
              { icon: Truck, text: "Livraison rapide" },
              { icon: ShieldCheck, text: "Paiement sécurisé" },
              { icon: RotateCcw, text: "Retour 7 jours" },
            ].map((b) => (
              <div key={b.text} className="text-center">
                <b.icon className="mx-auto text-[#B8860B] mb-2" size={22} strokeWidth={1.5} />
                <p className="text-xs text-neutral-500">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex gap-8 border-b border-neutral-200 mb-8">
          {[
            { key: "desc", label: "Description" },
            { key: "reviews", label: `Avis (${reviews.length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`pb-4 font-display text-xl transition relative ${tab === t.key ? "text-[#0A0A0A]" : "text-neutral-400"}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
              {tab === t.key && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#B8860B]" />}
            </button>
          ))}
        </div>

        {tab === "desc" ? (
          <p className="text-neutral-600 leading-relaxed max-w-3xl">{product.description}</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl">
            <div className="space-y-6">
              {reviews.length === 0 && <p className="text-neutral-500">Soyez le premier à laisser un avis.</p>}
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-neutral-100 pb-4" data-testid="review-item">
                  <div className="flex gap-0.5 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} className={i < r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-neutral-300"} />
                    ))}
                  </div>
                  <p className="font-medium text-[#0A0A0A]">{r.author}</p>
                  <p className="text-neutral-600 text-sm mt-1">{r.comment}</p>
                </div>
              ))}
            </div>
            <form onSubmit={submitReview} className="bg-[#FBF9F4] p-6 rounded-lg h-fit" data-testid="review-form">
              <h4 className="font-display text-xl mb-4">Laisser un avis</h4>
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}>
                    <Star size={22} className={n <= form.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-neutral-300"} />
                  </button>
                ))}
              </div>
              <input
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                placeholder="Votre nom"
                className="w-full border border-neutral-200 rounded-sm py-2 px-3 text-sm mb-3 focus:outline-none focus:border-[#B8860B]"
                data-testid="review-author"
              />
              <textarea
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="Votre avis…"
                rows={4}
                className="w-full border border-neutral-200 rounded-sm py-2 px-3 text-sm mb-3 focus:outline-none focus:border-[#B8860B]"
                data-testid="review-comment"
              />
              <button type="submit" className="btn-dark w-full py-3 rounded-sm" data-testid="review-submit">Publier mon avis</button>
            </form>
          </div>
        )}
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 px-4 md:px-8 bg-[#FBF9F4]">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <h2 className="font-display text-3xl md:text-4xl text-center text-[#0A0A0A] mb-3">Souvent achetés ensemble</h2>
              <GoldDivider className="mb-12" />
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.07}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
